import React, { useContext, useMemo } from 'react';
import { Doughnut, Bar, Bubble } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { ThemeContext } from '../../../context/ThemeContext';
import { CAT_COLORS, fmtInr, fmtPct } from './fixedIncomePortfolioConstants';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

function PortfolioCharts({ data }) {
  const { isDark } = useContext(ThemeContext);
  const m = data.metrics;

  const gridColor = isDark ? 'rgba(244,244,240,0.08)' : 'rgba(0,0,0,0.05)';
  const tickColor = isDark ? '#b8b9b4' : '#454745';

  const catEntries = useMemo(
    () =>
      Object.entries(m.category_distribution)
        .filter(([, v]) => v > 0)
        .sort(([, a], [, b]) => b - a),
    [m.category_distribution]
  );

  const donutData = useMemo(
    () => ({
      labels: catEntries.map(([c]) => c),
      datasets: [
        {
          data: catEntries.map(([, v]) => v),
          backgroundColor: catEntries.map(([c]) => CAT_COLORS[c] || '#888'),
          borderWidth: 2,
          borderColor: isDark ? '#161714' : '#fff',
        },
      ],
    }),
    [catEntries, isDark]
  );

  const ladderLabels = ['<1 year', '1-5 years', '5-10 years', '10+ / perpetual'];
  const ladderColors = ['#1D9E75', '#378ADD', '#BA7517', '#D4537E'];
  const ladderValues = ladderLabels.map((label) => {
    const match = Object.entries(m.maturity_bucket_distribution).find(
      ([key]) => key === label || key.replace(/\s/g, '') === label.replace(/\s/g, '')
    );
    return match ? match[1] : 0;
  });

  const bubbleData = useMemo(
    () => ({
      datasets: data.holdings.map((h) => ({
        label: h.instrument.name.split(' ').slice(0, 3).join(' '),
        data: [
          {
            x: h.instrument.modified_duration,
            y: h.instrument.indicative_yield,
            r: Math.max(5, Math.sqrt(h.weight_pct) * 3.5),
          },
        ],
        backgroundColor: `${CAT_COLORS[h.instrument.category] || '#888'}99`,
        borderColor: CAT_COLORS[h.instrument.category] || '#888',
        borderWidth: 1,
      })),
    }),
    [data.holdings]
  );

  const incomeData = useMemo(() => {
    const items = data.holdings
      .map((h) => ({
        name:
          h.instrument.name.length > 30
            ? `${h.instrument.name.slice(0, 28)}…`
            : h.instrument.name,
        income: Math.round(h.estimated_annual_income_inr),
        color: CAT_COLORS[h.instrument.category] || '#888',
      }))
      .sort((a, b) => b.income - a.income);
    return {
      labels: items.map((d) => d.name),
      datasets: [
        {
          label: 'Annual income',
          data: items.map((d) => d.income),
          backgroundColor: items.map((d) => `${d.color}cc`),
          borderColor: items.map((d) => d.color),
          borderWidth: 1,
          borderRadius: 3,
        },
      ],
    };
  }, [data.holdings]);

  const baseScale = {
    grid: { color: gridColor },
    ticks: { color: tickColor, font: { size: 10 } },
  };

  return (
    <div className="fipb-charts-grid">
      <div className="fipb-chart-card">
        <div className="fipb-chart-title">Allocation by asset class</div>
        <div className="fipb-donut-wrap">
          <div className="fipb-chart-inner">
            <Doughnut
              data={donutData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '62%',
                plugins: { legend: { display: false } },
              }}
            />
          </div>
          <div className="fipb-donut-center">
            <div className="fipb-donut-yield">{fmtPct(m.weighted_yield_pct)}</div>
            <div className="fipb-donut-label">avg yield</div>
          </div>
        </div>
        <div className="fipb-donut-legend">
          {catEntries.map(([cat, pct]) => (
            <span key={cat} className="fipb-leg-item">
              <span className="fipb-leg-dot" style={{ background: CAT_COLORS[cat] || '#888' }} />
              {cat} {pct.toFixed(1)}%
            </span>
          ))}
        </div>
      </div>

      <div className="fipb-chart-card">
        <div className="fipb-chart-title">Maturity ladder</div>
        <div className="fipb-chart-inner fipb-chart-inner--tall">
          <Bar
            data={{
              labels: ladderLabels,
              datasets: [{ data: ladderValues, backgroundColor: ladderColors, borderRadius: 4 }],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { ...baseScale, grid: { display: false } },
                y: { ...baseScale, max: 100, ticks: { ...baseScale.ticks, callback: (v) => `${v}%` } },
              },
            }}
          />
        </div>
      </div>

      <div className="fipb-chart-card">
        <div className="fipb-chart-title">Yield vs duration</div>
        <div className="fipb-chart-inner">
          <Bubble
            data={bubbleData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: {
                  ...baseScale,
                  title: { display: true, text: 'Duration (years)', color: tickColor, font: { size: 10 } },
                },
                y: {
                  ...baseScale,
                  title: { display: true, text: 'Yield (%)', color: tickColor, font: { size: 10 } },
                  ticks: { ...baseScale.ticks, callback: (v) => `${v}%` },
                },
              },
            }}
          />
        </div>
      </div>

      <div className="fipb-chart-card">
        <div className="fipb-chart-title">Annual income by instrument</div>
        <div
          className="fipb-chart-inner fipb-chart-inner--income"
          style={{ height: Math.max(200, data.holdings.length * 30 + 40) }}
        >
          <Bar
            data={incomeData}
            options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: {
                  ...baseScale,
                  ticks: {
                    ...baseScale.ticks,
                    callback: (v) => `₹${Math.round(v / 1000)}k`,
                  },
                },
                y: { ...baseScale, grid: { display: false } },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default PortfolioCharts;
